export type BusinessCardPlanTier = 'starter' | 'standard' | 'business' | 'unknown';

export type BusinessCardEntitlementFeature =
    | 'brandableQr'
    | 'leadCapture'
    | 'leadEnrichment'
    | 'webhooks'
    | 'analytics'
    | 'emailNotifications'
    | 'smsNotifications'
    | 'removePoweredByPerCard'
    | 'walletColorCustomization'
    | 'walletLogoCustomization'
    | 'orgManagedTemplates'
    | 'teamTemplateAssignments';

export type BusinessCardEntitlement = {
    brandableQr: boolean;
    leadCapture: boolean;
    leadEnrichment: boolean;
    webhooks: boolean;
    analytics: boolean;
    emailNotifications: boolean;
    smsNotifications: boolean;
    removePoweredByPerCard: boolean;
    walletColorCustomization: boolean;
    walletLogoCustomization: boolean;
    orgManagedTemplates: boolean;
    teamTemplateAssignments: boolean;
};

export type BusinessCardWalletCustomizationScope = 'none' | 'perCard' | 'organization';

function normalizePlanName(planName: string | null | undefined): string {
    return (planName ?? '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function resolveBusinessCardPlanTier(planName: string | null | undefined): BusinessCardPlanTier {
    const normalizedPlanName = normalizePlanName(planName);

    if (!normalizedPlanName) {
        return 'unknown';
    }

    if (/\bstarter\b/.test(normalizedPlanName)) {
        return 'starter';
    }

    if (/\bstandard\b/.test(normalizedPlanName)) {
        return 'standard';
    }

    if (normalizedPlanName === 'business card') {
        return 'business';
    }

    const tierName = normalizedPlanName.replace(/^business\s+card\b/, '').trim();

    if (/\bbusiness\b/.test(tierName)) {
        return 'business';
    }

    return 'unknown';
}

const EMPTY_ENTITLEMENT: BusinessCardEntitlement = {
    brandableQr: false,
    leadCapture: false,
    leadEnrichment: false,
    webhooks: false,
    analytics: false,
    emailNotifications: false,
    smsNotifications: false,
    removePoweredByPerCard: false,
    walletColorCustomization: false,
    walletLogoCustomization: false,
    orgManagedTemplates: false,
    teamTemplateAssignments: false
};

export function getBusinessCardEntitlement(planName: string | null | undefined): BusinessCardEntitlement {
    const tier = resolveBusinessCardPlanTier(planName);

    const tierEntitlements: Record<BusinessCardPlanTier, BusinessCardEntitlement> = {
        starter: { ...EMPTY_ENTITLEMENT },
        standard: {
            ...EMPTY_ENTITLEMENT,
            brandableQr: true,
            leadCapture: true,
            leadEnrichment: true,
            webhooks: true,
            analytics: true,
            removePoweredByPerCard: true,
            walletColorCustomization: true,
            walletLogoCustomization: true
        },
        business: {
            ...EMPTY_ENTITLEMENT,
            brandableQr: true,
            leadCapture: true,
            leadEnrichment: true,
            webhooks: true,
            analytics: true,
            emailNotifications: true,
            smsNotifications: true,
            removePoweredByPerCard: true,
            walletColorCustomization: true,
            walletLogoCustomization: true,
            orgManagedTemplates: true,
            teamTemplateAssignments: true
        },
        unknown: { ...EMPTY_ENTITLEMENT }
    };

    return tierEntitlements[tier] ?? { ...EMPTY_ENTITLEMENT };
}

export function hasBusinessCardFeature(
    planName: string | null | undefined,
    featureName: BusinessCardEntitlementFeature
): boolean {
    return !!getBusinessCardEntitlement(planName)[featureName];
}

export function getBusinessCardWalletCustomizationScope(
    planName: string | null | undefined
): BusinessCardWalletCustomizationScope {
    switch (resolveBusinessCardPlanTier(planName)) {
        case 'standard':
            return 'perCard';
        case 'business':
            return 'organization';
        default:
            return 'none';
    }
}

export function isBusinessCardStarterPlan(planName: string | null | undefined): boolean {
    return resolveBusinessCardPlanTier(planName) === 'starter';
}

export function isBusinessCardStandardPlan(planName: string | null | undefined): boolean {
    return resolveBusinessCardPlanTier(planName) === 'standard';
}

export function isBusinessCardBusinessPlan(planName: string | null | undefined): boolean {
    return resolveBusinessCardPlanTier(planName) === 'business';
}

export function hasBusinessCardStandardFeatures(planName: string | null | undefined): boolean {
    const tier = resolveBusinessCardPlanTier(planName);
    return tier === 'standard' || tier === 'business';
}
